import * as React from "react";
import { css } from "@emotion/core";
import { Button, Spinner } from "@blueprintjs/core";
import {
  ControlledTreeEnvironment,
  Tree,
  TreeItemIndex,
  DraggingPosition,
} from "react-complex-tree";
import { useArticlePositionUpdate } from "../../../hooks/useKnowledgeBase";
import { calculateNewPositions, cx } from "../utils";
import { ROOT_DROPZONE_ID } from "../constants";
import { createTreeRenderers } from "./TreeRenderers";
import { ArticleTreeProps, TreeItemsMap } from "./types";

const ArticleTree: React.FC<ArticleTreeProps> = ({
  articles,
  isLoading,
  selectedArticleId,
  onSelectArticle,
  onCreateArticle,
  isFybrAdmin,
  forceExpandIds,
  onHoverArticle,
  onOpenSearch,
}) => {
  const caretClickRef = React.useRef(false);
  const [expandedItems, setExpandedItems] = React.useState<TreeItemIndex[]>([]);

  const updatePositions = useArticlePositionUpdate();

  const treeEnvironmentKey = React.useMemo(() => {
    if (!articles || articles.length === 0) return "kb-tree-empty";

    return articles
      .map(
        (article) =>
          `${article.id}:${article.parent_id ?? "root"}:${article.position}`
      )
      .join("|");
  }, [articles]);

  const treeData = React.useMemo<TreeItemsMap>(() => {
    if (!articles) return {};

    const items: TreeItemsMap = {
      root: {
        index: "root",
        isFolder: true,
        children: [],
        data: null,
      },
    };

    articles.forEach((article) => {
      items[String(article.id)] = {
        index: String(article.id),
        isFolder: false,
        children: [],
        data: article,
      };
    });

    [...articles]
      .sort((a, b) => a.position - b.position)
      .forEach((article) => {
        const parentId =
          article.parent_id === null ? "root" : String(article.parent_id);
        if (items[parentId]) {
          items[parentId].children?.push(String(article.id));
        }
      });

    if (isFybrAdmin && articles.length > 0) {
      const rootChildren = items["root"].children ?? [];
      items[ROOT_DROPZONE_ID] = {
        index: ROOT_DROPZONE_ID,
        isFolder: false,
        children: [],
        data: { isRootDropZone: true },
      };
      rootChildren.push(ROOT_DROPZONE_ID);
      items["root"].children = rootChildren;
    }

    Object.values(items).forEach((item) => {
      if (item.index === "root") {
        item.isFolder = true;
        return;
      }

      const hasChildren = (item.children?.length || 0) > 0;
      item.isFolder = hasChildren;
      (item as any).hasChildren = hasChildren;
    });

    return items;
  }, [articles, isFybrAdmin]);

  const rootArticlesCount = React.useMemo(
    () => articles?.filter((a) => a.parent_id === null).length ?? 0,
    [articles]
  );

  const viewState = React.useMemo(
    () => ({
      "kb-tree": {
        selectedItems: selectedArticleId ? [String(selectedArticleId)] : [],
        expandedItems,
      },
    }),
    [expandedItems, selectedArticleId]
  );

  React.useEffect(() => {
    if (!forceExpandIds || forceExpandIds.length === 0) return;

    setExpandedItems((prev) => {
      const next = new Set(prev);
      forceExpandIds.forEach((id) =>
        next.add(id === "root" ? "root" : String(id))
      );
      return Array.from(next);
    });
  }, [forceExpandIds]);

  const renderers = React.useMemo(
    () => createTreeRenderers(caretClickRef, onHoverArticle),
    [onHoverArticle]
  );

  const handleViewStateChange = React.useCallback(
    (
      _treeId: string,
      state: { selectedItems?: string[]; expandedItems?: TreeItemIndex[] }
    ) => {
      if (state.expandedItems !== undefined) {
        setExpandedItems(state.expandedItems);
      }

      const selectedItemId = state.selectedItems?.[0];
      if (selectedItemId === ROOT_DROPZONE_ID) {
        return;
      }
      const nextArticleId =
        selectedItemId && selectedItemId !== "root"
          ? Number(selectedItemId)
          : null;

      if (nextArticleId !== selectedArticleId) {
        onSelectArticle(nextArticleId);
      }
    },
    [onSelectArticle, selectedArticleId]
  );

  const handleDrop = React.useCallback(
    async (items: any[], target: DraggingPosition) => {
      if (!isFybrAdmin || !articles) return;

      const movedArticleIds = items
        .map((item) => Number(item.index))
        .filter((id) => !Number.isNaN(id));

      let targetParentId: number | null = null;
      let targetIndex: number = 0;

      if (target.targetType === "between-items") {
        const parentIsRoot = target.parentItem === "root";
        targetParentId = parentIsRoot ? null : Number(target.parentItem);
        const rawIndex = target.childIndex;
        targetIndex = parentIsRoot
          ? Math.min(rawIndex, rootArticlesCount)
          : rawIndex;
      } else if (target.targetType === "item") {
        if (target.targetItem === ROOT_DROPZONE_ID) {
          targetParentId = null;
          targetIndex = rootArticlesCount;
        } else {
          targetParentId =
            target.targetItem === "root" ? null : Number(target.targetItem);
          const targetItem = treeData[target.targetItem];
          const targetChildrenCount = targetItem?.children?.length || 0;
          targetIndex =
            targetParentId === null ? rootArticlesCount : targetChildrenCount;
        }
      }

      const targetParentIndex =
        targetParentId === null ? "root" : String(targetParentId);
      setExpandedItems((prev) =>
        prev.includes(targetParentIndex) ? prev : [...prev, targetParentIndex]
      );

      const updates = calculateNewPositions(
        articles,
        targetParentId,
        targetIndex,
        movedArticleIds
      );

      try {
        await updatePositions.mutateAsync(updates);
      } catch (error) {
        console.error("Failed to update article positions:", error);
      }
    },
    [articles, isFybrAdmin, rootArticlesCount, treeData, updatePositions]
  );

  return (
    <div
      css={css`
        width: 300px;
        border-right: 1px solid var(--neutral-5);
        padding: 1rem;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
      `}
    >
      {onOpenSearch && (
        <div
          css={css`
            margin-bottom: 0.75rem;
          `}
        >
          <Button icon="search" fill onClick={onOpenSearch}>
            Search
          </Button>
        </div>
      )}
      {isFybrAdmin && (
        <div
          css={css`
            margin-bottom: 1rem;
          `}
        >
          <Button icon="plus" intent="primary" fill onClick={onCreateArticle}>
            Create Article
          </Button>
        </div>
      )}
      {isLoading ? (
        <Spinner size={30} />
      ) : Object.keys(treeData).length > 0 ? (
        <ControlledTreeEnvironment
          key={treeEnvironmentKey}
          {...(renderers as any)}
          items={treeData}
          viewState={viewState}
          onViewStateChange={handleViewStateChange}
          getItemTitle={(item) =>
            item.data?.isRootDropZone
              ? "Root drop target"
              : item.data?.title || "Knowledge Base"
          }
          onSelectItems={(items) => {
            if (caretClickRef.current) {
              caretClickRef.current = false;
              return;
            }
            const itemId = items[0];
            if (itemId !== "root" && itemId !== ROOT_DROPZONE_ID) {
              onSelectArticle(Number(itemId));
            }
          }}
          onExpandItem={(item) =>
            setExpandedItems((prev) =>
              prev.includes(item.index) ? prev : [...prev, item.index]
            )
          }
          onCollapseItem={(item) =>
            setExpandedItems((prev) => prev.filter((id) => id !== item.index))
          }
          canDragAndDrop={isFybrAdmin}
          canDropOnFolder={true}
          canDropOnNonFolder={true}
          canReorderItems={true}
          onDrop={handleDrop}
          renderRenameInput={() => null}
        >
          <Tree treeId="kb-tree" rootItem="root" treeLabel="Knowledge Base" />
        </ControlledTreeEnvironment>
      ) : null}
    </div>
  );
};

export default ArticleTree;
